import React, { useMemo, useState, useEffect } from 'react'
import Cookies from 'js-cookie'
import axios from 'axios'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import Topbar from '../../layouts/Topbar'
import Sidebar from '../../layouts/Sidebar'
import apiConfig, { API_BASE_URL as NAMED_API_BASE_URL } from '../../../config/api'

const API_BASE_URL = typeof apiConfig === 'string' ? apiConfig : NAMED_API_BASE_URL || 'https://pms.burrayhr.com'

const AnimatedCard = ({ children, index = 0, className = '' }) => (
    <div
        className={`stats-card-reveal ${className}`}
        style={{ animationDelay: `${index * 70}ms` }}
    >
        {children}
    </div>
)

const animStyles = `
    .stats-card-reveal {
        animation: statsFadeInUp 0.45s ease both;
    }
    @keyframes statsFadeInUp {
        from { opacity: 0; transform: translateY(12px); }
        to   { opacity: 1; transform: translateY(0); }
    }
`

const getStatusBadgeClass = (status) => {
    const s = (status || '').toLowerCase()
    if (s === 'complete' || s === 'completed') return 'bg-success-subtle text-success border border-success-subtle'
    if (s === 'in_progress' || s === 'in progress' || s === 'active') return 'bg-primary-subtle text-primary border border-primary-subtle'
    if (s === 'not_started') return 'bg-warning-subtle text-warning border border-warning-subtle'
    return 'bg-secondary-subtle text-secondary border border-secondary-subtle'
}

const formatStatusLabel = (status) => {
    const s = (status || '').toLowerCase()
    if (s === 'in_progress' || s === 'in progress') return 'In Progress'
    if (s === 'complete') return 'Completed'
    return s ? s.charAt(0).toUpperCase() + s.slice(1) : '—'
}

const renderTruncatedText = (text, goalId, field, maxLength = 60, expandedMap, setExpandedMap) => {
    const safeText = (text ?? '').toString()
    if (!safeText) return <span className="text-muted">—</span>
    if (safeText.length <= maxLength) return <span style={{ whiteSpace: 'pre-wrap' }}>{safeText}</span>

    const key = `${goalId || 'unknown'}-${field}`
    const isExpanded = !!expandedMap[key]
    const shownText = isExpanded ? safeText : `${safeText.slice(0, maxLength)}...`

    return (
        <span>
            <span style={{ whiteSpace: 'pre-wrap' }}>{shownText}</span>
            <button
                type="button"
                onClick={() => setExpandedMap(prev => ({ ...prev, [key]: !prev[key] }))}
                className="btn btn-link btn-sm p-0 ms-2 text-decoration-none fw-semibold"
            >
                {isExpanded ? 'Less' : 'More'}
            </button>
        </span>
    )
}

const renderApprovalBadge = (status) => {
    switch (status?.toLowerCase()) {
        case 'approved':
            return <span className="badge bg-success-subtle text-success border border-success-subtle">Approved</span>
        case 'rejected':
            return <span className="badge bg-danger-subtle text-danger border border-danger-subtle">Rejected</span>
        case 'pending':
            return (
                <span className="badge bg-warning-subtle text-warning border border-warning-subtle d-inline-flex align-items-center gap-1">
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                    Pending
                </span>
            )
        default:
            return <span className="badge bg-secondary-subtle text-secondary border border-secondary-subtle">{status || 'Unknown'}</span>
    }
}

const GoalDetailsModal = ({ isOpen, goal, onClose }) => {
    if (!isOpen || !goal) return null

    const progressVal = Number(goal.progress ?? goal.completion_percent ?? goal.completion ?? 0)
    const status = goal.status || (progressVal >= 100 ? 'completed' : progressVal > 0 ? 'in_progress' : 'not_started')

    return (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 1055 }}>
            <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
                <div className="modal-content shadow-lg border-0">
                    <div className="modal-header border-bottom">
                        <div className="d-flex align-items-center gap-2">
                            <i className="ti ti-clipboard-list text-primary fs-20" />
                            <h5 className="modal-title fw-bold mb-0">Goal Details</h5>
                        </div>
                        <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
                    </div>
                    <div className="modal-body">
                        <div className="table-responsive border rounded">
                            <table className="table table-striped mb-0">
                                <tbody>
                                    <tr>
                                        <th className="text-muted fw-semibold fs-14" style={{ width: '35%' }}>Activity</th>
                                        <td className="fw-medium text-dark" style={{ whiteSpace: 'pre-wrap' }}>{goal.activity || '—'}</td>
                                    </tr>
                                    <tr>
                                        <th className="text-muted fw-semibold fs-14">Status</th>
                                        <td>
                                            <span className={`badge ${getStatusBadgeClass(status)} fs-13 px-3 py-1`}>
                                                {formatStatusLabel(status)}
                                            </span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <th className="text-muted fw-semibold fs-14">Performance Score</th>
                                        <td className="fw-medium text-dark">
                                            {goal.performance_score != null ? goal.performance_score : goal.score != null ? goal.score : '—'}
                                        </td>
                                    </tr>
                                    {goal.start_date && (
                                        <tr>
                                            <th className="text-muted fw-semibold fs-14">Start Date</th>
                                            <td className="fw-medium text-dark">{goal.start_date}</td>
                                        </tr>
                                    )}
                                    {goal.end_date && (
                                        <tr>
                                            <th className="text-muted fw-semibold fs-14">End / Due Date</th>
                                            <td className="fw-medium text-dark">{goal.end_date}</td>
                                        </tr>
                                    )}
                                    {goal.key_result_area && (
                                        <tr>
                                            <th className="text-muted fw-semibold fs-14">Key Result Area</th>
                                            <td className="fw-medium text-dark">
                                                {typeof goal.key_result_area === 'object' ? goal.key_result_area.name : goal.key_result_area}
                                            </td>
                                        </tr>
                                    )}
                                    {goal.stakeholder && (
                                        <tr>
                                            <th className="text-muted fw-semibold fs-14">Stakeholder</th>
                                            <td className="fw-medium text-dark">{goal.stakeholder}</td>
                                        </tr>
                                    )}
                                    {goal.metric && (
                                        <tr>
                                            <th className="text-muted fw-semibold fs-14">KPI Metric</th>
                                            <td className="fw-medium text-dark">{goal.metric}</td>
                                        </tr>
                                    )}
                                    {goal.quarter && (
                                        <tr>
                                            <th className="text-muted fw-semibold fs-14">Period</th>
                                            <td className="fw-medium text-dark">{goal.quarter}</td>
                                        </tr>
                                    )}
                                    {goal.is_overdue != null && (
                                        <tr>
                                            <th className="text-muted fw-semibold fs-14">Is Overdue</th>
                                            <td>
                                                {goal.is_overdue ? (
                                                    <span className="badge bg-danger-subtle text-danger border border-danger-subtle">Yes</span>
                                                ) : (
                                                    <span className="badge bg-success-subtle text-success border border-success-subtle">No</span>
                                                )}
                                            </td>
                                        </tr>
                                    )}
                                    {goal.approval_status && (
                                        <tr>
                                            <th className="text-muted fw-semibold fs-14">Approval Status</th>
                                            <td>{renderApprovalBadge(goal.approval_status)}</td>
                                        </tr>
                                    )}
                                    {goal.approval_status === 'rejected' && goal.rejected_reason && (
                                        <tr>
                                            <th className="text-muted fw-semibold fs-14">Rejection Reason</th>
                                            <td className="text-danger fw-semibold">{goal.rejected_reason}</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div className="modal-footer border-top">
                        <button type="button" className="btn btn-secondary d-inline-flex align-items-center gap-1" onClick={onClose}>
                            <i className="ti ti-x" /> Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}

const GOALS_PER_PAGE = 8

const GoalsTable = ({ goals = [], statusFilter = 'all' }) => {
    const [expandedMap, setExpandedMap] = useState({})
    const [modalOpen, setModalOpen] = useState(false)
    const [selectedGoal, setSelectedGoal] = useState(null)
    const [currentPage, setCurrentPage] = useState(1)

    const filteredGoals = useMemo(() => {
        if (statusFilter === 'all') return goals
        return goals.filter(g => {
            const progressVal = Number(g.progress ?? g.completion_percent ?? g.completion ?? 0)
            const rawStatus = (g.status || (progressVal >= 100 ? 'completed' : progressVal > 0 ? 'in_progress' : 'not_started') || '').toLowerCase()
            return rawStatus === statusFilter || rawStatus === statusFilter.replace('_', ' ') || (statusFilter === 'completed' && rawStatus === 'complete')
        })
    }, [goals, statusFilter])

    useEffect(() => {
        setCurrentPage(1)
    }, [statusFilter])

    const totalPages = Math.max(1, Math.ceil(filteredGoals.length / GOALS_PER_PAGE))
    const safePage = Math.min(currentPage, totalPages)
    const startIdx = (safePage - 1) * GOALS_PER_PAGE
    const pageGoals = filteredGoals.slice(startIdx, startIdx + GOALS_PER_PAGE)

    const handleViewGoal = (goal) => {
        setSelectedGoal(goal)
        setModalOpen(true)
    }

    const closeModal = () => {
        setModalOpen(false)
        setSelectedGoal(null)
    }

    const renderPagination = () => {
        if (totalPages <= 1) return null
        const pages = []
        const maxVisible = 5
        let startPage = Math.max(1, safePage - Math.floor(maxVisible / 2))
        let endPage = Math.min(totalPages, startPage + maxVisible - 1)
        if (endPage - startPage + 1 < maxVisible) {
            startPage = Math.max(1, endPage - maxVisible + 1)
        }

        for (let i = startPage; i <= endPage; i++) pages.push(i)

        return (
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 pt-3 border-top mt-0 mx-3 mb-3">
                <div className="fs-13 text-muted">
                    Showing <strong>{filteredGoals.length ? startIdx + 1 : 0}</strong> – <strong>{Math.min(startIdx + GOALS_PER_PAGE, filteredGoals.length)}</strong> of <strong>{filteredGoals.length}</strong> goals{statusFilter !== 'all' && <> · Filtered: <span className="fw-semibold text-dark">{formatStatusLabel(statusFilter)}</span></>}
                </div>
                <nav>
                    <ul className="pagination pagination-sm mb-0">
                        <li className={`page-item ${safePage === 1 ? 'disabled' : ''}`}>
                            <button
                                type="button"
                                className="page-link"
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                disabled={safePage === 1}
                            >
                                <i className="ti ti-chevron-left" />
                            </button>
                        </li>
                        {startPage > 1 && (
                            <>
                                <li className="page-item">
                                    <button type="button" className="page-link" onClick={() => setCurrentPage(1)}>1</button>
                                </li>
                                {startPage > 2 && <li className="page-item disabled"><span className="page-link">…</span></li>}
                            </>
                        )}
                        {pages.map(p => (
                            <li key={p} className={`page-item ${p === safePage ? 'active' : ''}`}>
                                <button type="button" className="page-link" onClick={() => setCurrentPage(p)}>{p}</button>
                            </li>
                        ))}
                        {endPage < totalPages && (
                            <>
                                {endPage < totalPages - 1 && <li className="page-item disabled"><span className="page-link">…</span></li>}
                                <li className="page-item">
                                    <button type="button" className="page-link" onClick={() => setCurrentPage(totalPages)}>{totalPages}</button>
                                </li>
                            </>
                        )}
                        <li className={`page-item ${safePage === totalPages ? 'disabled' : ''}`}>
                            <button
                                type="button"
                                className="page-link"
                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                disabled={safePage === totalPages}
                            >
                                <i className="ti ti-chevron-right" />
                            </button>
                        </li>
                    </ul>
                </nav>
            </div>
        )
    }

    if (!filteredGoals || !filteredGoals.length) {
        return (
            <div className="text-muted text-center py-5">
                <i className="ti ti-clipboard-off fs-36 d-block mb-2 text-secondary opacity-50" />
                {statusFilter === 'all' ? 'No goals found' : `No ${formatStatusLabel(statusFilter)} goals found`}
            </div>
        )
    }

    return (
        <>
            <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                    <thead className="table-light">
                        <tr>
                            <th style={{ width: '50px' }}>#</th>
                            <th>Goal / Activity</th>
                            <th style={{ width: '120px' }}>Status</th>
                            <th style={{ width: '180px' }}>Period</th>
                            <th className="text-center" style={{ width: '100px' }}>Score</th>
                            <th className="text-center" style={{ width: '90px' }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {pageGoals.map((g, idx) => {
                            const progressVal = Number(g.progress ?? g.completion_percent ?? g.completion ?? 0)
                            const status = g.status || (progressVal >= 100 ? 'completed' : progressVal > 0 ? 'in_progress' : 'not_started')
                            const goalKey = g.id || g.code || `${startIdx}-${idx}`
                            return (
                                <tr key={goalKey}>
                                    <td className="fw-semibold text-muted fs-13">{startIdx + idx + 1}</td>
                                    <td>
                                        <div>
                                            <div className="text-muted fs-13">
                                                {renderTruncatedText(g.activity || g.title, goalKey, 'description', 80, expandedMap, setExpandedMap)}
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <span className={`badge ${getStatusBadgeClass(status)} fs-12 px-3 py-1`}>
                                            {formatStatusLabel(status)}
                                        </span>
                                    </td>
                                    <td className="fs-13 text-muted">
                                        {(g.start_date && g.end_date)
                                            ? `${g.start_date} → ${g.end_date}`
                                            : g.quarter || '—'}
                                    </td>
                                    <td className="text-center">
                                        <strong className="fs-14 text-dark">
                                            {g.performance_score != null ? g.performance_score : g.score != null ? g.score : '—'}
                                        </strong>
                                    </td>
                                    <td className="text-center">
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-icon btn-light text-primary border"
                                            title="View Full Goal Details"
                                            onClick={() => handleViewGoal(g)}
                                        >
                                            <i className="ti ti-eye" />
                                        </button>
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </div>
            {renderPagination()}
            <GoalDetailsModal isOpen={modalOpen} goal={selectedGoal} onClose={closeModal} />
        </>
    )
}

const HODEmployeeReport = ({ responseData: propData }) => {
    const { employeeId } = useParams()
    const navigate = useNavigate()
    const location = useLocation()
    const resolvedEmployeeId = location.state?.employeeId || employeeId
    const [responseData, setResponseData] = useState(propData || null)
    const [loading, setLoading] = useState(!propData)
    const [error, setError] = useState(null)
    const [statusFilter, setStatusFilter] = useState('all')

    useEffect(() => {
        if (propData) return
        if (!resolvedEmployeeId) return

        let cancelled = false
        const fetchReport = async () => {
            setLoading(true)
            try {
                const token = Cookies.get('token')
                const headers = token ? { Authorization: `Bearer ${token}` } : {}
                const res = await axios.get(`${API_BASE_URL}/api/employee-performance-summary/${resolvedEmployeeId}/`, { headers })
                if (!cancelled) setResponseData(res.data)
            } catch (err) {
                if (!cancelled) setError(err.response?.data || err.message || 'Failed to load report')
            } finally {
                if (!cancelled) setLoading(false)
            }
        }

        fetchReport()
        return () => { cancelled = true }
    }, [resolvedEmployeeId, propData])

    const data = useMemo(() => {
        if (responseData) return responseData
        const sampleGoals = [
            { id: 1, title: 'Improve System Performance by 30%', activity: 'Optimize database queries, implement caching layer, and reduce API response times across core modules.', status: 'completed', progress: 100, performance_score: 4.5, start_date: '2026-01-01', end_date: '2026-03-31' },
            { id: 2, title: 'Migrate Legacy Modules to New Architecture', activity: 'Migrate the oldest modules to microservices architecture with test coverage.', status: 'in_progress', progress: 68, performance_score: 4.0, start_date: '2026-02-01', end_date: '2026-06-30' },
            { id: 3, title: 'Onboard and Mentor Junior Developers', activity: 'Complete onboarding for team members and assign mentorship tasks.', status: 'in_progress', progress: 45, performance_score: 3.8, start_date: '2026-03-01', end_date: '2026-05-31' },
            { id: 4, title: 'Deliver Q2 Customer Dashboard Redesign', activity: 'Design and implement the analytics dashboard for enterprise customers.', status: 'in_progress', progress: 30, performance_score: 3.5, start_date: '2026-04-01', end_date: '2026-06-30' },
            { id: 5, title: 'Reduce Production Incidents by 50%', activity: 'Implement monitoring, alerting, and automated rollback procedures.', status: 'not_started', progress: 0, performance_score: null, start_date: '2026-05-01', end_date: '2026-08-31' },
        ]
        const completed = sampleGoals.filter(g => g.status === 'completed')
        const in_progress = sampleGoals.filter(g => g.status === 'in_progress')
        const not_started = sampleGoals.filter(g => g.status === 'not_started')
        return {
            employee: { id: 1, full_name: 'Employee', code: 'EMP001', email: 'employee@example.com', department_name: 'Department', position: 'Team Member' },
            summary_metrics: { total_goals: sampleGoals.length, completed_goals: completed.length, in_progress_goals: in_progress.length, pending_goals: not_started.length, completed_vs_all: `${completed.length} / ${sampleGoals.length}`, completion_rate: Math.round((completed.length / sampleGoals.length) * 100), average_performance: 4.1 },
            goals_by_status: { completed, in_progress, not_started },
            line_chart_data: { labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'], completion_rates: [20, 35, 52, 48, 60, 68] }
        }
    }, [responseData])

    const { employee = {}, summary_metrics = {}, goals_by_status = {}, line_chart_data = {} } = data

    const flattenedGoals = useMemo(() => {
        const statusOrder = ['in_progress', 'completed', 'not_started']
        const all = []
        statusOrder.forEach(statusKey => {
            const list = goals_by_status[statusKey] || []
            list.forEach(g => {
                all.push({ ...g, status: g.status || statusKey })
            })
        })
        if (all.length === 0) {
            Object.entries(goals_by_status).forEach(([statusKey, list]) => {
                (list || []).forEach(g => all.push({ ...g, status: g.status || statusKey }))
            })
        }
        return all
    }, [goals_by_status])

    const lineChartSeries = useMemo(() => {
        const labels = line_chart_data.labels || []
        const rates = line_chart_data.completion_rates || []
        return labels.map((label, i) => ({
            period: label,
            completion: Number(rates[i] ?? 0)
        }))
    }, [line_chart_data])

    const handleBack = () => {
        if (typeof window !== 'undefined' && window.history && window.history.length > 1) {
            navigate(-1)
        } else {
            navigate('/hod/employees')
        }
    }

    if (loading) {
        return (
            <>
                <style>{animStyles}</style>
                <div data-sidebar-skin="dark" data-header-skin="light" data-navbar-brand-skin="dark" data-sidebar-state="expand">
                    <Topbar />
                    <Sidebar />
                    <div className="page-wrapper">
                        <div className="content pb-0">
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="text-muted fs-13 mt-2 mb-0">Loading employee report...</p>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        )
    }

    if (error) {
        return (
            <>
                <style>{animStyles}</style>
                <div data-sidebar-skin="dark" data-header-skin="light" data-navbar-brand-skin="dark" data-sidebar-state="expand">
                    <Topbar />
                    <Sidebar />
                    <div className="page-wrapper">
                        <div className="content pb-0">
                            <div className="alert alert-danger">
                                <strong>Error:</strong> {typeof error === 'string' ? error : JSON.stringify(error)}
                            </div>
                        </div>
                    </div>
                </div>
            </>
        )
    }

    return (
        <>
            <style>{animStyles}</style>
            <div data-sidebar-skin="dark" data-header-skin="light" data-navbar-brand-skin="dark" data-sidebar-state="expand">
                <Topbar />
                <Sidebar />

                <div className="page-wrapper">
                    <div className="content pb-0">

                        <AnimatedCard index={0}>
                            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
                                <div>
                                    <h4 className="fw-bold mb-1">Employee Performance Report</h4>
                                    <p className="text-muted mb-0 fs-13">
                                        Detailed performance summary and goal breakdown
                                    </p>
                                </div>
                                <div className="d-flex gap-2 align-items-center">
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={() => window.location.reload()}
                                        title="Refresh"
                                    >
                                        <i className="ti ti-refresh me-1" />
                                        Refresh
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-primary d-flex align-items-center gap-1"
                                        onClick={handleBack}
                                    >
                                        <i className="ti ti-arrow-left" /> Back
                                    </button>
                                </div>
                            </div>
                        </AnimatedCard>

                        <AnimatedCard index={1}>
                            <div className="card shadow-sm mb-4">
                                <div className="card-body">
                                    <div className="row g-4 align-items-center">
                                        <div className="col-md-6 col-lg-4">
                                            <div className="d-flex align-items-center gap-3">
                                                <div className="avatar avtar-xl bg-indigo">
                                                    <i className="ti ti-user fs-30 text-white" />
                                                </div>
                                                <div>
                                                    <h5 className="fw-bold mb-1">{employee.full_name || '—'}</h5>
                                                    <p className="text-muted mb-1 fs-14">
                                                        {employee.position || '—'}
                                                        {employee.department_name && (
                                                            <>
                                                                {' '}
                                                                <span className="text-muted">•</span>{' '}
                                                                {employee.department_name}
                                                            </>
                                                        )}
                                                    </p>
                                                    <div className="d-flex flex-wrap gap-3 fs-13">
                                                        {employee.code && (
                                                            <span className="badge bg-light text-dark border">
                                                                <i className="ti ti-id me-1" />
                                                                {employee.code}
                                                            </span>
                                                        )}
                                                        {employee.email && (
                                                            <span className="text-muted">
                                                                <i className="ti ti-mail me-1" />
                                                                {employee.email}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-6 col-lg-8">
                                            <div className="row g-3">
                                                <div className="col-6 col-sm-3 d-flex">
                                                    <div className="card flex-fill border-0 bg-light">
                                                        <div className="card-body p-3">
                                                            <div className="d-flex align-items-center justify-content-between">
                                                                <div>
                                                                    <div className="avatar avtar-sm bg-teal mb-2">
                                                                        <i className="ti ti-target fs-16 text-white" />
                                                                    </div>
                                                                    <h6 className="fs-12 fw-semibold mb-1 text-muted">Total Goals</h6>
                                                                    <h4 className="fw-bold mb-0">{summary_metrics.total_goals ?? '—'}</h4>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-6 col-sm-3 d-flex">
                                                    <div className="card flex-fill border-0 bg-light">
                                                        <div className="card-body p-3">
                                                            <div className="d-flex align-items-center justify-content-between">
                                                                <div>
                                                                    <div className="avatar avtar-sm bg-success mb-2">
                                                                        <i className="ti ti-circle-check fs-16 text-white" />
                                                                    </div>
                                                                    <h6 className="fs-12 fw-semibold mb-1 text-muted">Completed</h6>
                                                                    <h4 className="fw-bold mb-0 text-success">{summary_metrics.completed_goals ?? '—'}</h4>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-6 col-sm-3 d-flex">
                                                    <div className="card flex-fill border-0 bg-light">
                                                        <div className="card-body p-3">
                                                            <div className="d-flex align-items-center justify-content-between">
                                                                <div>
                                                                    <div className="avatar avtar-sm bg-primary mb-2">
                                                                        <i className="ti ti-player-play fs-16 text-white" />
                                                                    </div>
                                                                    <h6 className="fs-12 fw-semibold mb-1 text-muted">In Progress</h6>
                                                                    <h4 className="fw-bold mb-0 text-primary">{summary_metrics.in_progress_goals ?? '—'}</h4>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-6 col-sm-3 d-flex">
                                                    <div className="card flex-fill border-0 bg-light">
                                                        <div className="card-body p-3">
                                                            <div className="d-flex align-items-center justify-content-between">
                                                                <div>
                                                                    <div className="avatar avtar-sm bg-warning mb-2">
                                                                        <i className="ti ti-clock-hour-3 fs-16 text-white" />
                                                                    </div>
                                                                    <h6 className="fs-12 fw-semibold mb-1 text-muted">Not Started</h6>
                                                                    <h4 className="fw-bold mb-0 text-warning">{summary_metrics.pending_goals ?? '—'}</h4>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <hr className="my-4" />

                                    <div className="row g-3">
                                        <div className="col-sm-6 col-lg-4 d-flex">
                                            <div className="card flex-fill border">
                                                <div className="card-body p-3">
                                                    <div className="d-flex align-items-center justify-content-between">
                                                        <div>
                                                            <h6 className="fs-13 fw-semibold mb-1 text-muted">Completed vs All</h6>
                                                            <h3 className="fw-bold mb-0">{summary_metrics.completed_vs_all ?? '—'}</h3>
                                                        </div>
                                                        <div className="avatar avtar-md bg-indigo-light">
                                                            <i className="ti ti-list-check fs-20 text-indigo" />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-sm-6 col-lg-4 d-flex">
                                            <div className="card flex-fill border">
                                                <div className="card-body p-3">
                                                    <div className="d-flex align-items-center justify-content-between gap-3">
                                                        <div className="flex-grow-1">
                                                            <h6 className="fs-13 fw-semibold mb-2 text-muted">Completion Rate</h6>
                                                            <div className="d-flex align-items-center gap-2">
                                                                <h3 className="fw-bold mb-0">
                                                                    {summary_metrics.completion_rate != null
                                                                        ? `${summary_metrics.completion_rate}%`
                                                                        : '—'}
                                                                </h3>
                                                            </div>
                                                            <div className="progress mt-2" style={{ height: '8px', backgroundColor: '#eef2ff' }}>
                                                                <div
                                                                    className="progress-bar"
                                                                    role="progressbar"
                                                                    style={{
                                                                        width: `${Math.min(Number(summary_metrics.completion_rate || 0), 100)}%`,
                                                                        backgroundColor: '#4F46E5'
                                                                    }}
                                                                    aria-valuenow={Number(summary_metrics.completion_rate || 0)}
                                                                    aria-valuemin="0"
                                                                    aria-valuemax="100"
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-sm-12 col-lg-4 d-flex">
                                            <div className="card flex-fill border">
                                                <div className="card-body p-3">
                                                    <div className="d-flex align-items-center justify-content-between">
                                                        <div>
                                                            <h6 className="fs-13 fw-semibold mb-1 text-muted">Avg Performance</h6>
                                                            <h3 className="fw-bold mb-0">{summary_metrics.average_performance ?? '—'}</h3>
                                                        </div>
                                                        <div className="avatar avtar-md bg-success-light">
                                                            <i className="ti ti-chart-infographic fs-20 text-success" />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </AnimatedCard>

                        <div className="row g-4">
                            <AnimatedCard index={2} className="col-xl-12 d-flex">
                                <div className="card shadow-sm flex-fill h-100">
                                    <div className="card-header d-flex align-items-center justify-content-between">
                                        <div>
                                            <h5 className="fw-bold mb-0">Completion Overview</h5>
                                            <p className="text-muted fs-12 mb-0 mt-1">
                                                Historical completion rate trend across tracked periods
                                            </p>
                                        </div>
                                    </div>
                                    <div className="card-body">
                                        {lineChartSeries.length === 0 ? (
                                            <div className="text-muted text-center py-5">
                                                <i className="ti ti-chart-line fs-36 d-block mb-2 text-secondary opacity-50" />
                                                No trend data available
                                            </div>
                                        ) : (
                                            <div style={{ width: '100%', height: 300 }}>
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <LineChart data={lineChartSeries} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                                                        <defs>
                                                            <linearGradient id="reportLineGrad" x1="0" y1="0" x2="1" y2="0">
                                                                <stop offset="0%" stopColor="#4F46E5" />
                                                                <stop offset="100%" stopColor="#818CF8" />
                                                            </linearGradient>
                                                        </defs>
                                                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f5" />
                                                        <XAxis
                                                            dataKey="period"
                                                            tick={{ fontSize: 12, fill: '#6b7280' }}
                                                        />
                                                        <YAxis
                                                            domain={[0, 100]}
                                                            tickFormatter={(v) => `${v}%`}
                                                            tick={{ fontSize: 12, fill: '#6b7280' }}
                                                        />
                                                        <Tooltip
                                                            formatter={(value) => [`${Number(value).toFixed(1)}%`, 'Completion Rate']}
                                                            contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                                                        />
                                                        <Line
                                                            type="monotone"
                                                            dataKey="completion"
                                                            name="Completion Rate"
                                                            stroke="url(#reportLineGrad)"
                                                            strokeWidth={3}
                                                            dot={{ r: 5, fill: '#4F46E5', stroke: '#fff', strokeWidth: 2 }}
                                                            activeDot={{ r: 7, fill: '#6366F1', stroke: '#fff', strokeWidth: 3 }}
                                                        />
                                                    </LineChart>
                                                </ResponsiveContainer>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </AnimatedCard>

                            <AnimatedCard index={3} className="col-xl-12 d-flex">
                                <div className="card shadow-sm flex-fill h-100">
                                    <div className="card-header d-flex align-items-center justify-content-between flex-wrap gap-3">
                                        <div>
                                            <h5 className="fw-bold mb-0">Goals by Status</h5>
                                            <p className="text-muted fs-12 mb-0 mt-1">
                                                Complete list of employee goals — click the eye icon to view full details
                                            </p>
                                        </div>
                                        <div className="d-flex flex-wrap gap-2 align-items-center">
                                            <div className="btn-group btn-group-sm" role="group" aria-label="Filter goals by status">
                                                <button
                                                    type="button"
                                                    className={`btn d-inline-flex align-items-center gap-1 ${statusFilter === 'all' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                                                    onClick={() => setStatusFilter('all')}
                                                >
                                                    <i className="ti ti-list" />
                                                    All
                                                    <span className="badge bg-white bg-opacity-25 text-inherit ms-1">
                                                        {flattenedGoals.length || 0}
                                                    </span>
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`btn d-inline-flex align-items-center gap-1 ${statusFilter === 'in_progress' ? 'btn-primary' : 'btn-outline-primary'}`}
                                                    onClick={() => setStatusFilter('in_progress')}
                                                >
                                                    <i className="ti ti-player-play" />
                                                    In Progress
                                                    <span className={`badge ms-1 ${statusFilter === 'in_progress' ? 'bg-white bg-opacity-25' : 'bg-primary-subtle text-primary'}`}>
                                                        {goals_by_status.in_progress?.length || 0}
                                                    </span>
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`btn d-inline-flex align-items-center gap-1 ${statusFilter === 'completed' ? 'btn-success' : 'btn-outline-success'}`}
                                                    onClick={() => setStatusFilter('completed')}
                                                >
                                                    <i className="ti ti-circle-check" />
                                                    Completed
                                                    <span className={`badge ms-1 ${statusFilter === 'completed' ? 'bg-white bg-opacity-25' : 'bg-success-subtle text-success'}`}>
                                                        {goals_by_status.completed?.length || 0}
                                                    </span>
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`btn d-inline-flex align-items-center gap-1 ${statusFilter === 'not_started' ? 'btn-warning' : 'btn-outline-warning'} ${statusFilter === 'not_started' ? 'text-white' : ''}`}
                                                    onClick={() => setStatusFilter('not_started')}
                                                >
                                                    <i className="ti ti-clock-hour-3" />
                                                    Not Started
                                                    <span className={`badge ms-1 ${statusFilter === 'not_started' ? 'bg-white bg-opacity-25 text-white' : 'bg-warning-subtle text-warning'}`}>
                                                        {goals_by_status.not_started?.length || 0}
                                                    </span>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="card-body p-0">
                                        <GoalsTable goals={flattenedGoals} statusFilter={statusFilter} />
                                    </div>
                                </div>
                            </AnimatedCard>
                        </div>

                    </div>

                    <div className="footer d-flex align-items-center justify-content-between flex-column flex-sm-row row-gap-2 border-top py-2 px-3">
                        <p className="text-dark mb-0">
                            2026 © <span className="link-primary">Casoe Technologies</span>, All Rights Reserved
                        </p>
                    </div>
                </div>
            </div>
        </>
    )
}

export default HODEmployeeReport